import { Fragment, useEffect, useMemo, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import {
  BanknotesIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  CloudArrowUpIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { useCollaboratorsRealtimeQuery } from '@/features/Collaborators/Data';
import { fetchCollaboratorsSalary, type SalaryHistoryEntry } from '@/features/Collaborators/Services/salarySubcollectionService';
import {
  buildPayrollImportPayload,
  matchPayrollRecords,
  normalizePayrollResult,
  parsePayrollPdf,
  type CollaboratorReference,
  type PayrollMatch,
  type PayrollMatchResult,
  type NormalizedPayrollEmployeeRecord,
  type PayrollAuditItem,
  savePayrollEmployeeAssignments,
  savePayrollImport,
  isCompetenceConsolidated,
} from '@/features/Expenses';
import { useToast } from '@/hooks/useToast';
import { normalizeString } from '@/utils/stringUtils';

function formatCpf(cpf?: string | null): string | undefined {
  if (!cpf) return undefined;
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

const getEffectiveSalaryForCompetence = (history: SalaryHistoryEntry[], competence?: string) => {
  if (!competence) return null;
  const [yearStr, monthStr] = competence.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  if (Number.isNaN(year) || Number.isNaN(month)) return null;
  const endOfMonth = new Date(year, month, 0);
  const ordered = [...history].sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());
  const entry = ordered.find((item) => new Date(item.changeDate).getTime() <= endOfMonth.getTime());
  return entry?.amount ?? null;
};

type MatchState = {
  matches: PayrollMatch[];
  conflicts: PayrollMatch[];
  unmatched: PayrollMatch[];
};

interface MappingComboboxProps {
  value?: string;
  options: CollaboratorReference[];
  disabled?: boolean;
  onChange: (id: string) => void;
}

function MappingCombobox({ value, options, disabled, onChange }: MappingComboboxProps) {
  const [query, setQuery] = useState('');

  const selected = options.find((o) => o.id === value) ?? null;
  const filtered =
    query.trim() === ''
      ? options
      : options.filter((opt) => normalizeString(opt.name).includes(normalizeString(query)));

  return (
    <Combobox value={selected} onChange={(opt) => onChange(opt?.id ?? '')} disabled={disabled}>
      {({ open }) => (
        <div className="relative w-full">
          <Combobox.Input
            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            displayValue={(opt: CollaboratorReference | null) => opt?.name ?? ''}
            onChange={(event) => setQuery(event.target.value)}
            autoComplete="off"
            placeholder="Selecionar colaborador..."
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>
          <Transition
            as={Fragment}
            show={open}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filtered.length === 0 && query !== '' ? (
                <div className="relative cursor-default select-none px-4 py-2 text-gray-500">Nenhum resultado</div>
              ) : (
                filtered.map((person) => (
                  <Combobox.Option
                    key={person.id}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-primary-600 text-white' : 'text-gray-900'}`
                    }
                    value={person}
                  >
                    {({ selected: isSelected, active }) => (
                      <>
                        <span className={`block truncate ${isSelected ? 'font-semibold' : 'font-normal'}`}>
                          {person.name}
                          {person.cpf ? ` — ${person.cpf}` : ''}
                        </span>
                        {isSelected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-primary-600'
                              }`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
}

function buildMatchList(result: PayrollMatchResult): PayrollMatch[] {
  return [...result.unmatched, ...result.conflicts, ...result.matches];
}

function StatusBadge({ status }: { status: PayrollMatch['status'] }) {
  const statusMap: Record<
    PayrollMatch['status'],
    { text: string; className: string }
  > = {
    auto_id: { text: 'Auto (ID)', className: 'bg-green-100 text-green-700' },
    auto_cpf: { text: 'Auto (CPF)', className: 'bg-green-100 text-green-700' },
    manual_pending: { text: 'Manual', className: 'bg-blue-100 text-blue-700' },
    manual_override: { text: 'Manual', className: 'bg-blue-100 text-blue-700' },
    conflict_id: { text: 'Conflito ID', className: 'bg-amber-100 text-amber-700' },
    conflict_cpf: { text: 'Conflito CPF', className: 'bg-amber-100 text-amber-700' },
    not_found: { text: 'Sem match', className: 'bg-gray-100 text-gray-700' },
  };
  const data = statusMap[status] ?? statusMap.not_found;
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ${data.className}`}>{data.text}</span>;
}

export function PayrollImportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const { data: collaborators, isLoading: loadingCollaborators } = useCollaboratorsRealtimeQuery();
  const collaboratorRefs = useMemo<CollaboratorReference[]>(() => {
    return (collaborators || []).map((c) => ({
      id: c.id ?? '',
      name: c.name ?? 'Sem nome',
      cpf: formatCpf((c as any).cpf ?? undefined),
      payrollEmployeeId: (c as any).payrollEmployeeId ?? null,
      grossSalary: (c as any).newSalaryEntryAmount ?? null,
      admissionDate: (c as any).startDate ? new Date((c as any).startDate).toISOString().split('T')[0] : null,
    }));
  }, [collaborators]);

  const [competence, setCompetence] = useState('');
  const [unionAdjustment, setUnionAdjustment] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [matches, setMatches] = useState<MatchState>({
    matches: [],
    conflicts: [],
    unmatched: [],
  });
  const [employees, setEmployees] = useState<NormalizedPayrollEmployeeRecord[]>([]);
  const [issues, setIssues] = useState<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [duplicatesCount, setDuplicatesCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [savedImportId, setSavedImportId] = useState<string | null>(null);

  const summaryPayload = useMemo(
    () =>
      buildPayrollImportPayload({ competence }, buildMatchList(matches)),
    [competence, matches],
  );

  // Reexecuta matching quando colaboradores chegarem após o parsing
  useEffect(() => {
    if (employees.length && collaboratorRefs.length) {
      setMatches(matchPayrollRecords(employees, collaboratorRefs));
    }
  }, [collaboratorRefs, employees]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] || null;
    setFile(selected);
    if (selected) {
      setEmployees([]);
      setMatches({ matches: [], conflicts: [], unmatched: [] });
      setIssues([]);
      setParseError(null);
      setDuplicatesCount(0);
      setSaveMessage(null);
    }
  };

  const handleParse = async () => {
    if (!file) {
      showError('Selecione um arquivo PDF.');
      return;
    }

    setParseError(null);
    setIsParsing(true);
    setIssues([]);
    setDuplicatesCount(0);

    try {
      const parsed = await parsePayrollPdf(file);
      const normalized = normalizePayrollResult(parsed);

      setEmployees(normalized.employees);
      setDuplicatesCount(normalized.duplicates.length);
      setIssues([
        ...normalized.issues.map((i) => i.message),
        ...normalized.duplicates.map((d) => `Duplicado: ${d.employeeId} (${d.name})`),
      ]);
      setSaveMessage(null);

      const baseMatches = matchPayrollRecords(normalized.employees, collaboratorRefs);
      setMatches(baseMatches);
    } catch (err) {
      console.error(err);
      setParseError(err instanceof Error ? err.message : 'Falha ao processar PDF');
    } finally {
      setIsParsing(false);
    }
  };

  const handleManualSelect = (uniqueKey: string, collaboratorId: string | '') => {
    const collaborator = collaboratorRefs.find((c) => c.id === collaboratorId);

    setMatches((current) => {
      const updateList = (list: PayrollMatch[]) =>
        list.map((m) =>
          m.payroll.uniqueKey === uniqueKey
            ? {
              ...m,
              collaborator,
              status: collaborator ? ('manual_override' as const) : ('not_found' as const),
              matchedBy: collaborator ? ('employeeId' as const) : undefined,
            }
            : m,
        );

      return {
        matches: updateList(current.matches),
        conflicts: updateList(current.conflicts),
        unmatched: updateList(current.unmatched),
      };
    });
  };

  const matchRows = useMemo(() => buildMatchList(matches), [matches]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) {
      showError('É necessário estar autenticado para importar.');
      return;
    }

    const targetCompetence = competence.trim();
    if (!targetCompetence) {
      showError('Informe a competência (AAAA-MM).');
      return;
    }

    const alreadyConsolidated = await isCompetenceConsolidated(targetCompetence);
    if (alreadyConsolidated) {
      showError('Não é possível importar: competência já consolidada.');
      return;
    }

    if (employees.length === 0) {
      showError('Realize a conferência do arquivo antes de importar.');
      return;
    }

    setSaving(true);
    setSaveMessage(null);
    setSavedImportId(null);
    try {
      const completeList = buildMatchList(matches);

      // Fetch salary data for all matched collaborators
      const matchedCollaboratorIds = completeList
        .map(m => m.collaborator?.id)
        .filter((id): id is string => !!id);

      const salaryDataMap = await fetchCollaboratorsSalary(matchedCollaboratorIds);

      // Generate Audit
      const audit: PayrollAuditItem[] = [];

      completeList.forEach((match) => {
        const { payroll, collaborator } = match;

        if (!collaborator) {
          audit.push({
            employeeName: payroll.name,
            employeeId: payroll.employeeId,
            issueType: 'unmatched',
            description: 'Registro não vinculado a um colaborador',
          });
          return;
        }

        // 1. Check Base Salary (using effective salary from history)
        if (payroll.baseSalary) {
          const salaryInfo = salaryDataMap[collaborator.id];
          const effectiveSalary = salaryInfo
            ? getEffectiveSalaryForCompetence(salaryInfo.history, targetCompetence)
            : null;

          const comparisonSalary = effectiveSalary || collaborator.grossSalary;

          if (comparisonSalary) {
            const diff = Math.abs(payroll.baseSalary - comparisonSalary);
            if (diff > 0.01) {
              let issueType: PayrollAuditItem['issueType'] = 'salary_mismatch';
              let description = 'Salário base divergente';

              // Check for Union Adjustment
              if (unionAdjustment) {
                const rawDiff = payroll.baseSalary - comparisonSalary;
                const percentage = (rawDiff / comparisonSalary) * 100;
                const expectedPercentage = parseFloat(unionAdjustment);

                if (!isNaN(expectedPercentage) && Math.abs(percentage - expectedPercentage) < 0.1) {
                  issueType = 'union_adjustment_mismatch';
                  description = `Divergência compatível com Reajuste Sindical (${expectedPercentage}%)`;
                }
              }

              audit.push({
                employeeName: payroll.name,
                employeeId: payroll.employeeId,
                issueType,
                description,
                expectedValue: comparisonSalary,
                foundValue: payroll.baseSalary,
              });
            }
          }
        }

        // 2. Check Name (Simple normalization check)
        const normalizedPayrollName = normalizeString(payroll.name);
        const normalizedCollabName = normalizeString(collaborator.name);
        if (normalizedPayrollName !== normalizedCollabName) {
          audit.push({
            employeeName: payroll.name,
            employeeId: payroll.employeeId,
            issueType: 'name_mismatch',
            description: 'Nome divergente',
            expectedValue: collaborator.name,
            foundValue: payroll.name,
          });
        }

        // 3. Check Admission Date
        if (payroll.admissionDate && collaborator.admissionDate) {
          let payrollDate = payroll.admissionDate;
          if (payrollDate.includes('/')) {
            const [d, m, y] = payrollDate.split('/');
            payrollDate = `${y}-${m}-${d}`;
          }

          if (payrollDate !== collaborator.admissionDate) {
            audit.push({
              employeeName: payroll.name,
              employeeId: payroll.employeeId,
              issueType: 'admission_date_mismatch',
              description: 'Data de admissão divergente',
              expectedValue: collaborator.admissionDate,
              foundValue: payrollDate,
            });
          }
        }

        // 4. Check CPF
        if (payroll.cpf && collaborator.cpf) {
          const payrollCpf = payroll.cpf.replace(/\D/g, '');
          const collabCpf = collaborator.cpf.replace(/\D/g, '');
          if (payrollCpf !== collabCpf) {
            audit.push({
              employeeName: payroll.name,
              employeeId: payroll.employeeId,
              issueType: 'cpf_mismatch',
              description: 'CPF divergente',
              expectedValue: collaborator.cpf,
              foundValue: payroll.cpf,
            });
          }
        }
      });

      await savePayrollEmployeeAssignments(completeList);
      const payload = buildPayrollImportPayload({ competence: targetCompetence }, completeList);
      payload.audit = audit;

      const saved = await savePayrollImport(payload, { uid: user.uid, email: user.email, displayName: user.displayName });
      setSavedImportId(saved.id);
      setSaveMessage(
        `Confirmado: ${payload.records.length} registros prontos para import. Conflitos: ${matches.conflicts.length}, Sem match: ${matches.unmatched.length}.`,
      );
      showSuccess('Arquivo importado com sucesso.');
      navigate('/expenses/payroll');
    } catch (err) {
      console.error('[payroll-import] erro ao confirmar import:', err);
      setSaveMessage(err instanceof Error ? err.message : 'Falha ao salvar mapeamento');
      showError(err instanceof Error ? err.message : 'Falha ao salvar mapeamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button type="button" onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Importar Folha de Pagamento
            </h1>
            <p className="text-sm text-gray-500">
              {competence ? `Competência: ${competence}` : 'Selecione o arquivo e a competência'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Provedor</label>
            <div className="mt-1">
              <select
                disabled
                className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:cursor-not-allowed"
                value="jopel"
              >
                <option value="jopel">Jopel</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Competência (AAAA-MM)</label>
            <input
              type="month"
              value={competence}
              onChange={(event) => setCompetence(event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Reajuste Sindical (%)</label>
            <input
              type="number"
              step="0.01"
              value={unionAdjustment}
              onChange={(e) => setUnionAdjustment(e.target.value)}
              placeholder="Opcional"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Arquivo do extrato (PDF)</label>
            <label className="mt-1 flex cursor-pointer items-center justify-between rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:border-primary-400">
              <span className="inline-flex items-center gap-2">
                <CloudArrowUpIcon className="h-5 w-5 text-primary-600" aria-hidden />
                {file ? file.name : 'Selecionar arquivo (.pdf)'}
              </span>
              <input type="file" accept="application/pdf" className="hidden" onChange={handleFileChange} />
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleParse}
            disabled={isParsing || loadingCollaborators || !file}
            className="inline-flex items-center gap-2 rounded-md border border-primary-200 bg-white px-3 py-2 text-sm font-semibold text-primary-700 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isParsing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Lendo arquivo...
              </>
            ) : (
              'Conferir'
            )}
          </button>
          <button
            type="submit"
            disabled={saving || isParsing || loadingCollaborators || employees.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden />
                Salvando
              </>
            ) : (
              <>
                Importar
                <ArrowRightIcon className="h-4 w-4" aria-hidden />
              </>
            )}
          </button>
        </div>
      </form>

      {parseError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {parseError}
        </div>
      )}

      {issues.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <div className="mb-1 font-semibold">Avisos do parser</div>
          <ul className="list-disc pl-5">
            {issues.map((issue, idx) => (
              <li key={`${issue}-${idx}`}>{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {duplicatesCount > 0 && (
        <div className="inline-flex items-center gap-2 rounded-md bg-amber-100 px-3 py-2 text-sm text-amber-800">
          <ExclamationTriangleIcon className="h-4 w-4" />
          {duplicatesCount} registro(s) duplicado(s) na competência.
        </div>
      )}

      {employees.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Mapeamento</h2>
                <p className="text-sm text-gray-600">
                  Confirme ou ajuste o colaborador correspondente para cada registro da folha.
                </p>
              </div>
              <div className="text-sm text-gray-600">
                {loadingCollaborators
                  ? 'Carregando colaboradores...'
                  : collaboratorRefs.length === 1
                    ? '1 disponível'
                    : `${collaboratorRefs.length} disponíveis`}
              </div>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Colaborador (folha)</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">CPF</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Match</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Ajustar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {matchRows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                      Faça upload do PDF para iniciar o mapeamento.
                    </td>
                  </tr>
                )}
                {matchRows.map((row) => (
                  <tr key={row.payroll.uniqueKey}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{row.payroll.name}</div>
                      <div className="text-xs text-gray-500">
                        ID Folha: {row.payroll.employeeId} | Horas Mês: {row.payroll.monthlyHours ?? '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{formatCpf(row.payroll.cpf) ?? '—'}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-4 py-3">
                      <MappingCombobox
                        value={row.collaborator?.id}
                        options={collaboratorRefs}
                        disabled={loadingCollaborators || isParsing}
                        onChange={(id) => handleManualSelect(row.payroll.uniqueKey, id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {employees.length > 0 && (
        <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700">
          Registros carregados: {employees.length}. Matches automáticos: {matches.matches.length}. Conflitos:{' '}
          {matches.conflicts.length}. Sem match: {matches.unmatched.length}.
        </div>
      )}
    </div>
  );
}
